import {
    createContext,
    type FC,
    type PropsWithChildren,
    startTransition,
    useContext,
    useEffect,
    useState,
    useSyncExternalStore,
    useMemo,
} from 'react';
import { type Store, type Dispatch } from 'redux';
import { type useSelector } from 'react-redux';

type DblExternalContextType = {
    state: $TSFixMe;
    dispatch: Dispatch;
};

const DblExternalContext = createContext<DblExternalContextType | null>(null);

function useDblExternalContext(): DblExternalContextType {
    const context = useContext(DblExternalContext);

    if (!context) {
        throw new Error(
            "useDblExternalContext must be used within a DblExternalContextProvider. This error might occur if you haven't wrapped the app root with DblExternalContextProvider on the client-side where hydrateRoot is called or haven't provided experimental_useSuspenseSSR flag to route handler on the server-side."
        );
    }

    return context;
}

export const DblExternalContextProvider: FC<
    PropsWithChildren<{
        store: Store;
    }>
> = ({ store, children }) => {
    /**
     * IMPORTANT: The third argument of useSyncExternalStore is intended for the server-side state to ensure that we're hydrating the
     * same component on the client side as we rendered on the server side. Since we currently don't have a way to ensure
     * that Redux stores are identical on the client and server side we're passing the same store state as third argument.
     * Later in the code store updates are wrapped in startTransition to ensure that store changes don't happen while hydrating
     * and wouldn't cause hydration mismatch. This is not an intended way of handling this case, but it's a patch that allows us
     * to use Suspense SSR. The intended way is to have Redux stores identical on the client and server sides, but at this
     * moment it's very difficult to achieve this. For now, use a patch.
     */
    const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
    const [contextState, setContextState] = useState(state);

    useEffect(() => {
        startTransition(() => {
            setContextState(state);
        });
    }, [state]);

    const value = useMemo(
        () => ({
            state: contextState,
            dispatch: store.dispatch,
        }),
        [contextState, store.dispatch]
    );

    return <DblExternalContext.Provider value={value}>{children}</DblExternalContext.Provider>;
};

export const useDblExternalSelector: typeof useSelector = selector => {
    const { state } = useDblExternalContext();
    return selector(state);
};

export const useDblExternalDispatch = (): Dispatch => {
    const { dispatch } = useDblExternalContext();
    return dispatch;
};
